import React, { useMemo } from "react";
import { cn } from "../../lib/utils";

/**
 * Split `text` on case-insensitive `query` matches.
 * @param {string} text
 * @param {string} query
 * @returns {Array<{ value: string, match: boolean }>}
 */
export function splitHighlightParts(text, query) {
  const source = text == null ? "" : String(text);
  const needle = (query || "").trim();
  if (!source || !needle) {
    return [{ value: source, match: false }];
  }
  const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`(${escaped})`, "ig");
  const parts = source.split(re);
  if (parts.length === 1) {
    return [{ value: source, match: false }];
  }
  const lower = needle.toLowerCase();
  return parts
    .filter((part) => part !== "")
    .map((part) => ({
      value: part,
      match: part.toLowerCase() === lower,
    }));
}

/**
 * Render text with query matches highlighted using theme search-match tokens.
 * Uses a span instead of <mark> so the browser's default yellow cannot leak
 * through in dark themes.
 * @param {{ text?: string, query?: string, className?: string }} props
 * @returns {JSX.Element}
 */
function HighlightedText({ text = "", query = "", className }) {
  const parts = useMemo(
    () => splitHighlightParts(text, query),
    [text, query]
  );

  return (
    <span className={cn(className)}>
      {parts.map((part, index) =>
        part.match ? (
          <span
            key={`${part.value}-${index}`}
            className="rounded-sm bg-search-match text-search-match-fg"
          >
            {part.value}
          </span>
        ) : (
          <span key={`${part.value}-${index}`}>{part.value}</span>
        )
      )}
    </span>
  );
}

export default HighlightedText;
